import {
	Box,
	Text,
	Button,
	Stack,
	Icons,
	Table,
	Tooltip,
	Container,
	Inline,
	Heading,
	Divider,
	Dropdown,
	Badge,
	Component,
	joinAttributes,
	Spinner,
	Stars,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Time,
	Money,
	Alert,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ButtonLink, Link } from "@sembark-travel/ui-router"
import {
	dateToQuery,
	dateToUTCString,
	parseDateFromQuery,
	startOf,
	formatDate,
	localOrUtcTimeToLocalDate,
} from "@sembark-travel/datetime-utils"
import { isNumeric, withOrdinalSuffix } from "@sembark-travel/number-utils"
import {
	NavLink,
	useLocationQuery,
	queryToSearch,
} from "@sembark-travel/ui-router"
import {
	Search,
	ListView,
	useSearch,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
} from "@sembark-travel/ui-list"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import * as Validator from "yup"
import React, { useEffect, useMemo, useState } from "react"
import config from "../config"
import { ContactsList, PhoneNumber, Email, IContact } from "../Contacts"
import { SelectHotelBookingStages } from "../HotelBookingStages"
import { PaymentsList } from "../Payments"
import { generatePath, useSearchParams } from "../router-utils"
import { getDisplayNameForStage, IBooking } from "../HotelBookings"
import { ITrip } from "../Trips"
import { HotelConfigLockButton } from "../shared/HotelConfigLock"
import { UpdateHotelInDialog } from "./Edit"
import { ChildAgesFields, type TChildAgesFormValues } from "./NewItem"
import {
	IHotel,
	useHotel,
	hotelsXHR,
	useHotelRemotePaxTypes,
	type THotelRemotePaxTypes,
} from "./store"

import {
	childAgesValidationSchema,
	formValuesToOtaChildAgesPayload,
	otaChildAgesToFormValues,
} from "./utils"
import { useCheckPermissions, PERMISSIONS } from "../Auth"
import {
	SelectField,
	DatePickerField,
	SwitchInputField,
	Form,
	GetFieldValue,
	SubmissionError,
	arrayMutators,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { CreateHotelNotes, HotelNotesList } from "../HotelNotes"
import { ISupplier } from "../Supplier"
import { TemporaryStorage } from "../TemporaryStorage"
import { moneyParseByDecimal } from "@sembark-travel/money"
import {
	ChannelManagerIcon,
	ChannelManagerSelect,
	getChannelManagerName,
	type TChannelManagerOption,
} from "../ChannelManagers"
import { AmenitiesList } from "../Amenities"
import {
	CancellationPolicyVersionView,
	SelectCancellationPolicyDetail,
} from "../CancellationPolicy"
import {
	PaymentPolicyVersionView,
	SelectPaymentPolicyDetail,
} from "../PaymentPolicy"
import type { TPolicyHeadSummary } from "../shared/policies"

type TChannelManagerFormSchema = {
	channel_manager: TChannelManagerOption | null
}

function XHR(xhr: XHRInstance) {
	return {
		async createContact(
			id: string | number,
			contactData: Record<string, unknown>
		): Promise<IContact> {
			return xhr
				.post(`/hotel-contacts`, {
					hotel_id: id,
					...contactData,
				})
				.then((resp) => resp.data.data)
		},
		async getBookings(
			params: unknown
		): Promise<IListResponse<IBooking & { trip: ITrip }>> {
			return xhr.get("/hotel-bookings", { params }).then((resp) => resp.data)
		},
		async destroyNote(id: number | string) {
			return xhr
				.delete<{ message: string }>(`/hotel-notes/${id}`)
				.then((res) => res.data)
		},
	}
}

export function HotelDetails({
	hotelId,
	children,
}: {
	hotelId: string
	children?: (props: { hotel: IHotel; onChange: () => void }) => React.ReactNode
}) {
	const { hotel, isFetching, fetchHotel: getHotel } = useHotel(hotelId)
	const { hasPermission, hasAnyPermission } = useCheckPermissions()
	const xhr = useXHR()
	if (isFetching && !hotel) return <Spinner alignCenter padding="4" />
	if (!hotel) return <Alert status="error">Hotel does not exist.</Alert>
	const { name, deleted_at } = hotel

	return (
		<Box>
			<Inline
				gap="2"
				alignItems="center"
				justifyContent="between"
				padding="4"
				borderBottomWidth="1"
			>
				<Inline gap="2" alignItems="center">
					<Link
						to={generatePath("/org/hotels")}
						color="muted"
						title="Back to Hotels"
					>
						<Icons.ArrowLeft />
					</Link>
					<Heading as="h3">
						<Inline gap="2" alignItems="center">
							{name}
							<Badge borderWidth="0" primary size="base">
								Code: {hotel.uid}
							</Badge>
							<ChannelManagerIcon
								channelManager={hotel.channel_manager}
								size="26"
							/>
						</Inline>
					</Heading>
				</Inline>
				<Inline gap="2" alignItems="center">
					{/* Only Admin can view hotel calendar */}
					{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
						<ButtonLink
							to={generatePath("/hotels/:hotelId/calendar", {
								hotelId: String(hotel.id),
							})}
							status="primary"
							title="Calendar"
							anchored
						>
							Calendar <Icons.CalendarDays />
						</ButtonLink>
					) : null}
					{hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
						<Component<"archive" | "restore" | undefined>
							initialState={undefined}
						>
							{({ state, setState }) => (
								<Dropdown alignRight>
									<Dropdown.ToggleButton size="sm" level="tertiary">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>

									<Dropdown.Menu>
										<Component initialState={false}>
											{({ state, setState }) => (
												<>
													<Dropdown.MenuItem onClick={() => setState(true)}>
														<Icons.Link /> Channel Manager
													</Dropdown.MenuItem>
													<Dialog
														title="Update Channel Manager"
														open={state}
														onClose={() => setState(false)}
														sm
													>
														<Form<TChannelManagerFormSchema>
															initialValues={{
																channel_manager: hotel.channel_manager
																	? {
																			id: hotel.channel_manager,
																			name:
																				getChannelManagerName(
																					hotel.channel_manager
																				) || hotel.channel_manager,
																		}
																	: null,
															}}
															onSubmit={withServerErrors(async (values) => {
																await hotelsXHR(xhr).updateChannelManager(
																	hotel.id,
																	{
																		channel_manager:
																			values.channel_manager?.id || null,
																	}
																)
																showSnackbar(
																	"Channel manager updated successfully"
																)
																getHotel()
																setState(false)
															})}
															subscription={{ submitting: true }}
														>
															{({ submitting, handleSubmit }) => (
																<form noValidate onSubmit={handleSubmit}>
																	<Dialog.Body>
																		<Stack gap="4">
																			<SelectField
																				select={ChannelManagerSelect}
																				name="channel_manager"
																				label="Channel Manager"
																				fetchOnMount
																			/>

																			<GetFieldValue<TChannelManagerOption> name="channel_manager">
																				{({ value: channelManager }) =>
																					hotel.channel_manager &&
																					channelManager?.id !==
																						hotel.channel_manager ? (
																						<Alert
																							type="assertive"
																							status="warning"
																						>
																							This will delete all the synced
																							inventory, rates, and restrictions
																							and you'll have to sync them again
																							from your Channel Manager.
																						</Alert>
																					) : null
																				}
																			</GetFieldValue>

																			<SubmissionError />
																		</Stack>
																	</Dialog.Body>
																	<Dialog.Footer>
																		<Inline justifyContent="end">
																			<Button
																				type="submit"
																				disabled={submitting}
																			>
																				{submitting ? <Icons.Spinner /> : null}{" "}
																				{submitting ? "Updating..." : "Update"}
																			</Button>
																		</Inline>
																	</Dialog.Footer>
																</form>
															)}
														</Form>
													</Dialog>
												</>
											)}
										</Component>
										{!deleted_at ? (
											<>
												<Dropdown.MenuItem
													onClick={() => setState("archive")}
													color="danger"
												>
													<Icons.Ban /> Disable
												</Dropdown.MenuItem>
												<Dialog
													open={state === "archive"}
													onClose={() => setState(undefined)}
													sm
												>
													<Dialog.Header>
														<Dialog.Title>Disable Hotel</Dialog.Title>
													</Dialog.Header>
													<Form<{ hotel_id: number }>
														initialValues={{ hotel_id: hotel.id }}
														onSubmit={withServerErrors(async (values) => {
															const resp = await xhr.delete<{
																message: string
															}>(`/hotels/${values.hotel_id}`)
															getHotel()
															showSnackbar(resp.data.message)
															setState(undefined)
														})}
														subscription={{ submitting: true }}
													>
														{({ submitting, handleSubmit }) => (
															<form noValidate onSubmit={handleSubmit}>
																<Dialog.Body>
																	<Text>
																		This hotel will be removed from all hotels
																		listing and selection pages. Existing usage
																		of this hotel will not be affected.
																	</Text>
																	<Divider sm />
																	<Text color="warning">
																		Are you sure you want to disable this hotel
																		?
																	</Text>
																</Dialog.Body>
																<Dialog.Footer>
																	<Button
																		type="submit"
																		disabled={submitting}
																		status="warning"
																	>
																		{submitting ? <Icons.Spinner /> : null}{" "}
																		{submitting
																			? "Disabling..."
																			: "I understand, disabled it."}
																	</Button>
																	<Button
																		onClick={() => setState(undefined)}
																		disabled={submitting}
																	>
																		Cancel
																	</Button>
																</Dialog.Footer>
															</form>
														)}
													</Form>
												</Dialog>
											</>
										) : (
											<>
												<Dropdown.MenuItem onClick={() => setState("restore")}>
													<Icons.Refresh /> Restore
												</Dropdown.MenuItem>
												<Dialog
													open={state === "restore"}
													onClose={() => setState(undefined)}
													sm
												>
													<Dialog.Header>
														<Dialog.Title>Restore disabled Hotel</Dialog.Title>
													</Dialog.Header>
													<Form<{ hotel_id: number }>
														initialValues={{ hotel_id: hotel.id }}
														onSubmit={withServerErrors(async (values) => {
															const resp = await xhr.patch<{ message: string }>(
																`/hotels/${values.hotel_id}/restore`
															)
															getHotel()
															showSnackbar(resp.data.message)
															setState(undefined)
														})}
														subscription={{ submitting: true }}
													>
														{({ submitting, handleSubmit }) => (
															<form noValidate onSubmit={handleSubmit}>
																<Dialog.Body>
																	<Text>
																		Are you sure you want to restore this
																		disabled hotel ?
																	</Text>
																</Dialog.Body>
																<Dialog.Footer>
																	<Button type="submit" disabled={submitting}>
																		{submitting ? <Icons.Spinner /> : null}{" "}
																		{submitting ? "Restoring..." : "Restore"}
																	</Button>
																	<Button
																		onClick={() => setState(undefined)}
																		disabled={submitting}
																	>
																		Cancel
																	</Button>
																</Dialog.Footer>
															</form>
														)}
													</Form>
												</Dialog>
											</>
										)}
									</Dropdown.Menu>
								</Dropdown>
							)}
						</Component>
					) : null}
				</Inline>
			</Inline>
			<Box borderTopWidth="1">
				<Tabs layout="row">
					<TabsList>
						<NavLink
							to={generatePath("/hotels/:hotelId", {
								hotelId: String(hotel.id),
							})}
							end
						>
							Details
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/room-types", {
								hotelId: String(hotel.id),
							})}
							paddingRight="8"
						>
							Room Type
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/meal-plans", {
								hotelId: String(hotel.id),
							})}
						>
							Meal Plans
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/rate-plans", {
								hotelId: String(hotel.id),
							})}
						>
							Rate Plans
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/child-ages", {
								hotelId: String(hotel.id),
							})}
						>
							Child Ages
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/policies", {
								hotelId: String(hotel.id),
							})}
						>
							Policies
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/add-ons", {
								hotelId: String(hotel.id),
							})}
						>
							Extra Services
						</NavLink>
						{hasPermission(PERMISSIONS.VIEW_HOTEL_BOOKINGS) ? (
							<NavLink
								to={generatePath("/hotels/:hotelId/bookings", {
									hotelId: String(hotel.id),
								})}
							>
								Bookings
							</NavLink>
						) : null}
						{hasAnyPermission(
							PERMISSIONS.VIEW_HOTEL_BOOKINGS,
							PERMISSIONS.MANAGE_ACCOUNTING
						) ? (
							<NavLink
								to={generatePath("/hotels/:hotelId/payments/outgoing", {
									hotelId: String(hotel.id),
								})}
							>
								Payments
							</NavLink>
						) : null}
						{/* {hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
							<NavLink
								to={generatePath("/hotels/:hotelId/calendar", {
									hotelId: String(hotel.id),
								})}
							>
								Calendar
							</NavLink>
						) : null} */}
						<NavLink
							to={generatePath("/hotels/:hotelId/notes", {
								hotelId: String(hotel.id),
							})}
						>
							Hotel Notes
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/hotel-activities", {
								hotelId: String(hotel.id),
							})}
						>
							Activity Logs
						</NavLink>
					</TabsList>
					<TabContent>
						{children
							? children({
									hotel,
									onChange: getHotel,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

export function Address({
	address,
	mapsUrl,
}: {
	address: IHotel["address"]
	mapsUrl?: IHotel["maps_url"]
}) {
	const location = address.location
	const city_state_names = [location.city, location.state]
		.map((d) => d?.name)
		.filter((x): x is string => Boolean(x))
	const city_state_line = city_state_names.join(", ")
	let line_1 = address.line_1?.replace(city_state_line, "").trim() || ""
	let line_2 = address.line_2?.replace(city_state_line, "").trim() || ""
	let landmark = address.landmark?.replace(city_state_line, "").trim() || ""
	if (city_state_names.indexOf(line_1) !== -1) {
		line_1 = ""
	}
	if (city_state_names.indexOf(line_2) !== -1) {
		line_2 = ""
	}
	if (city_state_names.indexOf(landmark) !== -1) {
		landmark = ""
	}
	return (
		<Stack as="address">
			{line_1 || line_2 || landmark ? (
				<Text>{[line_1, line_2, landmark].filter(Boolean).join(", ")}</Text>
			) : null}
			{city_state_line ? <Text>{city_state_line}</Text> : location.name}
			<Inline alignItems="center" gap="1">
				<Text>
					{[address.location?.country?.name, address.postcode]
						.filter(Boolean)
						.join(", ")}
				</Text>
				{mapsUrl ? (
					<Link
						to={mapsUrl}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Open location in Google Maps"
						style={{ display: "inline-flex" }}
						alignItems="center"
					>
						<Icons.ArrowTopRightOnSquare
							size="3"
							color="primary"
							fontWeight="semibold"
						/>
					</Link>
				) : null}
			</Inline>
			{address.phone_numbers?.length ? (
				<Box>
					<PhoneNumber value={address.phone_numbers} />
				</Box>
			) : null}
			{address.email ? (
				<Box>
					<Email value={address.email} />
				</Box>
			) : null}
		</Stack>
	)
}

export function HotelBasicDetails({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const {
		name,
		stars,
		group,
		url,
		location,
		address,
		latest_media,
		maps_url,
		brand_code,
		trip_destinations,
		checkin_at_local,
		checkin_at,
		checkout_at_local,
		checkout_at,
		created_at,
		created_by,
		updated_at,
		updated_by,
		deleted_at,
		deleted_by,
	} = hotel
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()

	return (
		<Container paddingY="6" paddingX="6">
			<Stack gap="6">
				<Box>
					<Inline justifyContent="between" alignItems="center" marginBottom="2">
						<Heading as="h4" fontSize="md">
							Basic Details
						</Heading>
						{hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
							<Component initialState={false}>
								{({ state, setState }) => (
									<>
										<Button
											onClick={() => setState(true)}
											size="sm"
											level="secondary"
										>
											<Icons.Pencil /> Edit
										</Button>
										<UpdateHotelInDialog
											hotel={hotel}
											open={state}
											onClose={() => setState(false)}
											onSuccess={() => {
												onChange()
												setState(false)
											}}
										/>
									</>
								)}
							</Component>
						) : null}
					</Inline>
					<Box bgColor="default" borderWidth="1" rounded="lg" padding="4">
						<Inline gap="4" flexWrap="wrap">
							{latest_media?.url ? (
								<Box
									as="img"
									src={latest_media.url}
									alt={hotel.name}
									loading="lazy"
									rounded="md"
									style={{ width: 160, height: 120, objectFit: "cover" }}
								/>
							) : (
								<Box
									style={{ width: 160, height: 120 }}
									display="flex"
									justifyContent="center"
									alignItems="center"
									bgColor="subtle"
								>
									<Icons.Photograph
										style={{ width: "100px", height: "100px" }}
									/>
								</Box>
							)}
							<Stack gap="3" flex="1" style={{ minWidth: "50%" }}>
								<Heading as="h4" fontSize="lg">
									{name}
								</Heading>
								<Inline flex="1" gap="12" flexWrap="wrap">
									<Stack gap="4" flex="1">
										{group ? (
											<Link
												color="accent"
												to={generatePath("/hotel-groups/:groupId", {
													groupId: group.id.toString(),
												})}
											>
												{group.name}
											</Link>
										) : null}
										{deleted_at ? (
											<Box>
												<Badge warning>Disabled</Badge>
											</Box>
										) : null}
										{stars || brand_code ? (
											<Inline gap="4">
												{stars ? (
													<Stack gap="1">
														<Box
															fontSize="sm"
															fontWeight="semibold"
															color="muted"
														>
															Stars
														</Box>
														<Stars stars={stars} />
													</Stack>
												) : null}
												{brand_code ? (
													<Stack gap="1">
														<Box
															fontSize="sm"
															fontWeight="semibold"
															color="muted"
														>
															Brand Code
														</Box>
														<Text fontSize="sm">{brand_code}</Text>
													</Stack>
												) : null}
											</Inline>
										) : null}
										<Stack gap="1">
											<Box fontSize="sm" fontWeight="semibold" color="muted">
												Location
											</Box>
											<Box>{location.name}</Box>
										</Stack>
										<Inline gap="4">
											<Stack gap="1">
												<Box fontSize="sm" fontWeight="semibold" color="muted">
													Check-In
												</Box>
												<Box>
													{formatDate(
														localOrUtcTimeToLocalDate(
															checkin_at_local,
															checkin_at
														),
														"HH:mm"
													)}{" "}
													hrs
												</Box>
											</Stack>
											<Stack gap="1">
												<Box fontSize="sm" fontWeight="semibold" color="muted">
													Check-Out
												</Box>
												<Box>
													{formatDate(
														localOrUtcTimeToLocalDate(
															checkout_at_local,
															checkout_at
														),
														"HH:mm"
													)}{" "}
													hrs
												</Box>
											</Stack>
										</Inline>
										{url ? (
											<Stack gap="1">
												<Box fontSize="sm" fontWeight="semibold" color="muted">
													Photos / Details URL
												</Box>
												<Box
													as="a"
													href={url}
													target="_blank"
													rel="noopener noreferrer"
													color="accent"
													maxWidth="sm"
													textOverflow="truncate"
													display="block"
												>
													{url.replace(/https?:\/\/(www\.)?/, "")}
												</Box>
											</Stack>
										) : null}
									</Stack>
									<Stack gap="4" flex="1">
										<Stack gap="1">
											<Box fontSize="sm" fontWeight="semibold" color="muted">
												Address
											</Box>
											<Address address={address} mapsUrl={maps_url} />
										</Stack>
										<Stack gap="1">
											<Box fontSize="sm" fontWeight="semibold" color="muted">
												Trip Destinations
											</Box>
											<Box>
												{trip_destinations?.length ? (
													trip_destinations.map((t) => t.name).join(", ")
												) : (
													<Box color="muted">All</Box>
												)}
											</Box>
										</Stack>
									</Stack>
								</Inline>
							</Stack>
						</Inline>
					</Box>
				</Box>
				<Box>
					<Heading as="h4" fontSize="md">
						Amenities
					</Heading>
					<Box bgColor="default" borderWidth="1" rounded="lg" padding="4">
						<AmenitiesList amenities={hotel.amenities || []} type="hotel" />
					</Box>
				</Box>
				<Box bgColor="default" borderWidth="1" rounded="lg" padding="4">
					<ContactsList
						contacts={hotel.contacts}
						phoneNumberRequired
						onCreate={async (data) => {
							const resp = await XHR(xhr).createContact(hotel.id, data)
							onChange()
							return resp
						}}
						onChange={() => onChange()}
						readOnly={!hasPermission(PERMISSIONS.CREATE_HOTELS)}
					/>
				</Box>
				<Box fontSize="sm" color="muted">
					{joinAttributes(
						<Text as="span">
							Created on <Time timestamp={created_at} />
							{created_by ? ` by ${created_by.name}` : null}
						</Text>,
						deleted_at ? (
							<Text as="span" color="danger">
								Disabled on <Time timestamp={deleted_at} />
								{deleted_by ? ` by ${deleted_by.name}` : null}
							</Text>
						) : null,
						updated_at && updated_by ? (
							<Text as="span">
								Updated on <Time timestamp={updated_at} /> by {updated_by.name}
							</Text>
						) : null
					)}
				</Box>
			</Stack>
		</Container>
	)
}

export function ComingSoonTab({ title }: { title: string }) {
	return (
		<Container paddingY="8" bgColor="default">
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				gap="2"
				height="full"
			>
				<Icons.Info size="8" color="muted" />
				<Text color="muted" fontWeight="semibold">
					{title} is coming soon.
				</Text>
			</Stack>
		</Container>
	)
}

type TFilters = TSearchParams & {
	supplier?: string
	has_due_payments?: boolean
	booking_stages?: Array<{ id: string; name: string }>
	stay_after?: Date
	stay_before?: Date
}

type TFiltersInQuery = TSearchParams & {
	supplier?: string
	hdp?: 1
	bs?: Array<string>
	stay_after?: string
	stay_before?: string
}

function filtersToLocationQuery(filters: TFilters): TFiltersInQuery {
	const {
		supplier,
		q,
		page,
		has_due_payments,
		booking_stages,
		stay_after,
		stay_before,
	} = filters
	const query: TFiltersInQuery = { q, page }
	if (supplier) {
		query.supplier = supplier
	}
	if (q) query.q = q
	if (page) query.page = page
	if (has_due_payments) query.hdp = 1
	if (booking_stages?.length) {
		query.bs = booking_stages.map((b) => `${b.id}#${b.name}`)
	}
	if (stay_after) {
		query.stay_after = dateToQuery(stay_after)
	}
	if (stay_before) {
		query.stay_before = dateToQuery(stay_before)
	}
	return query
}

function locationQueryToFilters(query: TFiltersInQuery): TFilters {
	const { supplier, q, page, hdp, bs, stay_after, stay_before } = query
	const filters: TFilters = { q }
	if (supplier) {
		filters.supplier = supplier
	}
	if (page) filters.page = page
	if (hdp) filters.has_due_payments = true
	if (bs?.length) {
		filters.booking_stages = bs.map((b) => {
			const [id, ...name] = b.split("#")
			return {
				id,
				name: name.join("#"),
			}
		})
	}
	if (stay_after) {
		filters.stay_after = parseDateFromQuery(stay_after)
	}
	if (stay_before) {
		filters.stay_before = parseDateFromQuery(stay_before)
	}
	return filters
}

function filtersToRequestParams(filters: TFilters) {
	const {
		supplier,
		q,
		page,
		has_due_payments,
		booking_stages,
		stay_after,
		stay_before,
	} = filters
	const isDirectBooking = supplier === "0" || false
	return {
		page: page || 1,
		q,
		supplier: !isDirectBooking
			? supplier === "all"
				? undefined
				: supplier
			: undefined,
		only_direct_hotel_bookings: isDirectBooking ? 1 : undefined,
		has_due_payments: has_due_payments ? 1 : null,
		booking_stages: booking_stages?.map((b) => b.id),
		stay_after: stay_after ? dateToUTCString(startOf(stay_after, "day")) : null,
		stay_before: stay_before
			? dateToUTCString(startOf(stay_before, "day"))
			: null,
	}
}

export function HotelBookings({
	params: propParams,
}: {
	params?: {
		hotels?: Array<number>
		hotel_groups?: Array<number>
		booking_suppliers?: ISupplier[]
		direct_bookings_exists?: boolean
	}
}) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToLocationQuery,
		fromQuery: locationQueryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		supplier:
			query.supplier || propParams?.direct_bookings_exists ? "0" : "all",
	})
	const showProperty = propParams?.hotels?.length !== 1
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box paddingTop="8">
			<Search
				placeholder="Search by Guest, Services etc..."
				Filters={Filters}
				resetParams={(params) => ({
					q: "",
					supplier:
						params.supplier || propParams?.direct_bookings_exists ? "0" : "all",
					page: 1,
				})}
				initialParams={params}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
				title="Bookings"
				areAdvancedFiltersApplied={(params) => {
					const { supplier, has_due_payments, ...otherParams } = params
					return (
						has_due_payments || areAdvancedFiltersAppliedDefault(otherParams)
					)
				}}
				actions={
					<>
						<TemporaryStorage
							initUrl="/hotel-bookings/export"
							initParams={{
								...filtersToRequestParams(params),
								hotels: propParams?.hotels,
								hotel_groups: propParams?.hotel_groups,
								timezone_offset: config.timezoneOffset,
							}}
						>
							{({ message, generate, url, reset, isGenerating }) =>
								url ? (
									<Button
										as="a"
										href={url}
										target="_blank"
										download
										level="secondary"
										onClick={() => reset()}
									>
										{message}
									</Button>
								) : (
									<Button
										onClick={generate}
										disabled={isGenerating}
										level="secondary"
									>
										{message || (
											<>
												<Icons.DocumentDownload />
											</>
										)}{" "}
										Excel
									</Button>
								)
							}
						</TemporaryStorage>
					</>
				}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs>
						<TabsList>
							{propParams?.direct_bookings_exists ? (
								<TabItem
									active={searchParams.supplier === "0"}
									onClick={() =>
										setSearchParams({
											...searchParams,
											supplier: "0",
										})
									}
								>
									Direct booking
								</TabItem>
							) : null}
							<TabItem
								active={searchParams.supplier === "all"}
								onClick={() =>
									setSearchParams({
										...searchParams,
										supplier: "all",
									})
								}
							>
								All
							</TabItem>
							{propParams?.booking_suppliers?.map((supplier) => {
								return (
									<TabItem
										key={supplier.id}
										active={searchParams.supplier === supplier.id.toString()}
										onClick={() =>
											setSearchParams({
												...searchParams,
												supplier: supplier.id.toString(),
											})
										}
									>
										{supplier.name}
									</TabItem>
								)
							})}
						</TabsList>
						<TabContent>
							<ListView<IBooking & { trip: ITrip }, TFilters>
								pageKey={`hotel-bookings/${queryToSearch(propParams)}`}
								fetch={(xhr, filters) =>
									XHR(xhr).getBookings({
										...filtersToRequestParams(filters),
										hotels: propParams?.hotels,
										hotel_groups: propParams?.hotel_groups,
										limit: 50,
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										bordered
										hover
										responsive
										headers={["Trip Id" as React.ReactNode]
											.concat(
												showProperty ? ["Property" as React.ReactNode] : []
											)
											.concat([
												"Guest",
												"Checkin",
												"Rooms/Services",
												"Meal",
												"Stage",
												<Tooltip content="Booking Confirmation Number">
													BCN
												</Tooltip>,
												"Amount",
											])}
										alignCols={{ [showProperty ? 8 : 7]: "right" }}
										rows={items.map(
											({
												id,
												checkin,
												checkin_local,
												no_of_nights,
												no_of_children,
												no_of_adults,
												pax_string,
												trip,
												booked,
												booking_stage,
												currency,
												booked_price,
												booking_confirmation_id,
												booking_method,
												room_nights,
												payments_amount,
												cancellation_charges,
												paid_payments_amount,
												refunding_payments_amount,
												paid_refunding_payments_amount,
												extras,
												details,
												hotel,
											}) =>
												[trip.id as React.ReactNode]
													.concat(
														showProperty
															? [
																	(
																		<Box>
																			<Box>
																				<Link
																					to={generatePath(
																						"/hotels/:hotelId/bookings",
																						{
																							hotelId: hotel.id.toString(),
																						}
																					)}
																					color="accent"
																				>
																					<Box
																						title={hotel.name}
																						style={{ maxWidth: "150px" }}
																						textOverflow="truncate"
																						fontWeight="semibold"
																					>
																						{hotel.name}
																					</Box>
																				</Link>
																				<Box
																					display="flex"
																					alignItems="center"
																					fontSize="sm"
																					color="muted"
																				>
																					{joinAttributes(
																						<Box
																							display="inlineBlock"
																							style={{ maxWidth: "150px" }}
																							textOverflow="truncate"
																						>
																							{hotel?.address.location.name}
																						</Box>,
																						hotel?.stars ? (
																							<Stars stars={hotel?.stars} />
																						) : null
																					)}
																				</Box>
																			</Box>
																		</Box>
																	) as React.ReactNode,
																]
															: []
													)
													.concat([
														<Box
															display={showProperty ? "block" : "flex"}
															flexWrap="nowrap"
															alignItems="center"
															justifyContent="between"
														>
															<Box marginRight="2">
																<Link
																	to={generatePath("/trips/:tripId", {
																		tripId: trip.id.toString(),
																	})}
																	fontWeight="semibold"
																	color="accent"
																>
																	{trip.tourist?.name || (
																		<>
																			<Icons.AttentionSolid color="warning" />{" "}
																			N/A
																		</>
																	)}
																</Link>
															</Box>
															<Tooltip content={pax_string}>
																<Box
																	whiteSpace="preserve"
																	color="muted"
																	fontSize="sm"
																>
																	{no_of_adults}A
																	{no_of_children
																		? `, ${no_of_children}C`
																		: null}
																</Box>
															</Tooltip>
														</Box>,
														<Box>
															<Time
																timestamp={checkin}
																localTimestamp={checkin_local}
																format="D MMM"
															/>{" "}
															- {no_of_nights}N
														</Box>,
														<Box>
															{(room_nights || []).map(function (
																{ room_type, no_of_rooms, nights },
																index
															) {
																return (
																	<Tooltip
																		key={index}
																		content={
																			nights
																				.map((n) => withOrdinalSuffix(n))
																				.join(", ") +
																			" " +
																			pluralize("Night", nights.length)
																		}
																	>
																		<Box key={room_type.id} display="inline">
																			{no_of_rooms} {room_type.name}
																		</Box>
																	</Tooltip>
																)
															})}
															{extras?.length ? (
																<Box fontSize="sm">
																	{extras.map((extra) => (
																		<Box key={extra.id} maxWidth="xs">
																			{extra.service.name}
																		</Box>
																	))}
																</Box>
															) : null}
														</Box>,
														<Box>
															{details
																?.reduce<
																	Array<string>
																>((plans, d) => (plans.indexOf(d.meal_plan.name) === -1 ? plans.concat([d.meal_plan.name]) : plans), [])
																.join(", ")}
														</Box>,
														<Box>
															{getDisplayNameForStage(booking_stage.state)}
															{booked && booking_method ? (
																<Box
																	display="inline"
																	marginLeft="1"
																	fontSize="xs"
																	color="muted"
																	fontWeight="semibold"
																>
																	{booking_method}
																</Box>
															) : null}
														</Box>,
														booking_confirmation_id,
														<Box>
															{isNumeric(booked_price) ? (
																<Inline
																	fontWeight="semibold"
																	gap="1"
																	justifyContent="end"
																>
																	{refunding_payments_amount === null ? (
																		[
																			"changed",
																			"booked",
																			"dropped",
																			"trip_dropped",
																		].includes(booking_stage?.state) &&
																		paid_payments_amount === payments_amount ? (
																			<Box display="inlineBlock" marginLeft="1">
																				<Icons.OkCircleSolid color="success" />
																			</Box>
																		) : null
																	) : paid_refunding_payments_amount ===
																	  refunding_payments_amount ? (
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null}
																	<Text>Booking:</Text>
																	<Link
																		to={
																			generatePath(
																				"/trips/:tripId/accounting/payments",
																				{
																					tripId: trip.id.toString(),
																				}
																			) + `#hb_${id}`
																		}
																	>
																		<Money
																			amount={booked_price}
																			currency={currency}
																			showCurrency
																		/>
																	</Link>
																</Inline>
															) : null}
															{isNumeric(payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	{isNumeric(refunding_payments_amount) &&
																	isNumeric(cancellation_charges) ? (
																		<Box>
																			Net Payable:{" "}
																			<Money
																				amount={cancellation_charges}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	) : isNumeric(booked_price) &&
																	  Number(payments_amount || 0) ===
																			Number(booked_price || 0) ? null : (
																		<Box>
																			Net Payable:{" "}
																			<Money
																				amount={payments_amount}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	)}
																</Box>
															) : //show net payaable as 0 if booking_status is either changed, dropped or trip dropped
															booking_stage.state === "changed" ||
															  booking_stage.state === "dropped" ||
															  booking_stage.state === "trip_dropped" ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	Net Payable:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(paid_payments_amount) ? (
																<>
																	<Box
																		fontWeight="semibold"
																		fontSize="sm"
																		color="success"
																	>
																		Amount Paid:{" "}
																		<Money
																			amount={paid_payments_amount}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																	{isNumeric(payments_amount) ? (
																		Number(payments_amount || 0) >
																		Number(paid_payments_amount || 0) ? (
																			<Box
																				fontWeight="semibold"
																				fontSize="sm"
																				color="muted"
																			>
																				Due Payable:{" "}
																				<Money
																					amount={
																						payments_amount -
																						paid_payments_amount
																					}
																					currency={currency}
																					showCurrency
																				/>
																			</Box>
																		) : null
																	) : null}
																</>
															) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
															isNumeric(payments_amount) &&
															  Number(payments_amount || 0) > 0 ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	Amount Paid:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(refunding_payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="warning"
																	title="Refund"
																>
																	Refund:{" "}
																	<Money
																		money={moneyParseByDecimal(
																			Number(
																				paid_refunding_payments_amount || 0
																			),
																			currency
																		)}
																		showCurrency
																	/>
																	{" / "}
																	<Money
																		amount={refunding_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
														</Box>,
													])
										)}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<Stack gap="1">
				<label htmlFor="stay_after">Stay During</label>
				<Box display="flex" alignItems="center" marginTop="1">
					<DatePickerField name="stay_after" />
					<Box>
						<Icons.SwitchHorizontal />
					</Box>
					<DatePickerField name="stay_before" rightAlign />
				</Box>
			</Stack>
			<SwitchInputField label="Has Due Payments" name="has_due_payments" />
			<SelectField
				select={SelectHotelBookingStages}
				name="booking_stages"
				label="Booking Stages"
				multiple
			/>
		</Stack>
	)
}

export function HotelPayments({
	hotel,
	paymentType,
}: {
	hotel: IHotel
	paymentType: "debit" | "credit"
}) {
	const { id } = hotel
	const hotels = useMemo(() => {
		return [id]
	}, [id])
	return (
		<Box paddingTop="8">
			<PaymentsList
				debit={paymentType === "debit" ? true : undefined}
				credit={paymentType === "credit" ? true : undefined}
				paymentContext="hotel"
				hotels={hotels}
				hideContact
			/>
		</Box>
	)
}

export function HotelNotes({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const { id, room_types, meal_plans, notes } = hotel
	const { hasPermission } = useCheckPermissions()
	return (
		<Box>
			{notes?.length ? (
				<Box backgroundColor={"default"}>
					<Inline
						justifyContent={"between"}
						borderBottomWidth={"1"}
						borderColor={"muted"}
						paddingY={"4"}
						paddingX={"8"}
					>
						<Heading>Notes</Heading>
						<Box>
							<Component initialState={false}>
								{({ state, setState }) => (
									<>
										{hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
											<Button onClick={() => setState(true)} size="sm">
												<Icons.Plus />
												Add new notes
											</Button>
										) : null}
										<Dialog open={state} onClose={() => setState(false)} xl>
											<Dialog.Header>
												<Dialog.Title>Add Hotel Notes</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<CreateHotelNotes
													roomTypes={room_types}
													mealPlans={meal_plans}
													hotelId={id}
													onSuccess={() => {
														onChange()
														setState(false)
													}}
													isGlobalNotes={false}
												/>
											</Dialog.Body>
										</Dialog>
									</>
								)}
							</Component>
						</Box>
					</Inline>
				</Box>
			) : null}
			<HotelNotesList
				notes={notes}
				roomTypes={room_types}
				mealPlans={meal_plans}
				hotelId={id}
				onChange={onChange}
			/>
		</Box>
	)
}

const POLICY_TABS = [
	{ id: "cancellation_policy", label: "Cancellation Policy" },
	{ id: "payment_policy", label: "Payment Policy" },
] as const

type TPolicyTabId = (typeof POLICY_TABS)[number]["id"]

export function HotelPolicies({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const xhr = useXHR()
	const [searchParams, setSearchParams] = useSearchParams()

	const rawTab = searchParams.get("policy_tab")
	const activeTab: TPolicyTabId = POLICY_TABS.some((t) => t.id === rawTab)
		? (rawTab as TPolicyTabId)
		: "cancellation_policy"

	return (
		<Tabs layout="col">
			<TabsList>
				{POLICY_TABS.map((tab) => (
					<TabItem
						key={tab.id}
						active={activeTab === tab.id}
						onClick={() => {
							setSearchParams(
								(prev) => {
									prev.set("policy_tab", tab.id)
									return prev
								},
								{ replace: true }
							)
						}}
					>
						{tab.label}
					</TabItem>
				))}
			</TabsList>
			<TabContent active={true} bgColor="default">
				{activeTab === "cancellation_policy" ? (
					<HotelPolicyTab
						key="cancellation_policy"
						label="Cancellation Policy"
						policyHead={hotel.cancellation_policy_head || null}
						selectPolicy={SelectCancellationPolicyDetail}
						versionView={CancellationPolicyVersionView}
						updatePolicy={(policyHeadId) =>
							hotelsXHR(xhr).updateCancellationPolicy(hotel.id, {
								cancellation_policy_head_id: policyHeadId,
							})
						}
						onChange={onChange}
					/>
				) : (
					<HotelPolicyTab
						key="payment_policy"
						label="Payment Policy"
						policyHead={hotel.payment_policy_head || null}
						selectPolicy={SelectPaymentPolicyDetail}
						versionView={PaymentPolicyVersionView}
						updatePolicy={(policyHeadId) =>
							hotelsXHR(xhr).updatePaymentPolicy(hotel.id, {
								payment_policy_head_id: policyHeadId,
							})
						}
						onChange={onChange}
					/>
				)}
			</TabContent>
		</Tabs>
	)
}

/**
 * Shows the policy linked to the hotel, and lets the users with access pick
 * another one. Used for both the cancellation and the payment policies.
 */
function HotelPolicyTab({
	label,
	policyHead,
	selectPolicy,
	versionView: PolicyVersionView,
	updatePolicy,
	onChange,
}: {
	label: string
	policyHead: TPolicyHeadSummary | null
	selectPolicy: React.ComponentProps<typeof SelectField>["select"]
	versionView: (props: { versionId: number }) => React.ReactNode
	updatePolicy: (policyHeadId: number) => Promise<unknown>
	onChange: () => void
}) {
	const { hasPermission } = useCheckPermissions()
	// Only admin can change policies
	const canChangePolicy = hasPermission(PERMISSIONS.MANAGE_USERS)
	const [isEditing, setIsEditing] = useState(false)

	if (policyHead && !(canChangePolicy && isEditing)) {
		return (
			<Stack gap="4" padding="6">
				<Inline
					gap="4"
					alignItems="center"
					justifyContent="between"
					flexWrap="wrap"
				>
					<PolicyHeadName policyHead={policyHead} />
					{canChangePolicy ? (
						<Button
							status="primary"
							level="primary"
							onClick={() => setIsEditing(true)}
						>
							Change Policy
						</Button>
					) : null}
				</Inline>
				<PolicyVersionView versionId={policyHead.latest_policy.id} />
			</Stack>
		)
	}

	if (!canChangePolicy) {
		return (
			<NoPolicyLinked>
				No {label.toLowerCase()} linked to this hotel.
			</NoPolicyLinked>
		)
	}

	return (
		<HotelPolicyForm
			label={label}
			policyHead={policyHead}
			selectPolicy={selectPolicy}
			versionView={PolicyVersionView}
			onSubmit={async (policyHeadId) => {
				await updatePolicy(policyHeadId)
				showSnackbar(`${label} updated successfully`)
				setIsEditing(false)
				onChange()
			}}
			onCancel={policyHead ? () => setIsEditing(false) : undefined}
		/>
	)
}

type THotelPolicyFormSchema = {
	policy_head: TPolicyHeadSummary | null
}

function HotelPolicyForm({
	label,
	policyHead,
	selectPolicy,
	versionView: PolicyVersionView,
	onSubmit,
	onCancel,
}: {
	label: string
	policyHead: TPolicyHeadSummary | null
	selectPolicy: React.ComponentProps<typeof SelectField>["select"]
	versionView: (props: { versionId: number }) => React.ReactNode
	onSubmit: (policyHeadId: number) => Promise<void> | void
	onCancel?: () => void
}) {
	const validationSchema = useMemo(
		() =>
			Validator.object().shape({
				policy_head: Validator.object()
					.nullable()
					.required(`Please select a ${label.toLowerCase()}`),
			}),
		[label]
	)

	return (
		<Form<THotelPolicyFormSchema>
			initialValues={{ policy_head: policyHead }}
			validate={validateFormValues(validationSchema)}
			subscription={{ submitting: true }}
			onSubmit={withServerErrors(async (values) => {
				if (!values.policy_head) return
				await onSubmit(values.policy_head.id)
			})}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4" padding="6">
						<Inline
							gap="4"
							alignItems="end"
							justifyContent="between"
							flexWrap="wrap"
						>
							<SelectField
								select={selectPolicy}
								name="policy_head"
								label={label}
								placeholder={`Search ${label.toLowerCase()}...`}
								clearable={false}
								required
								fetchOnMount
							/>
							<Inline gap="4">
								<Button
									type="submit"
									status="primary"
									level="primary"
									disabled={submitting}
								>
									{submitting ? <Icons.Spinner /> : null}{" "}
									{submitting ? "Updating..." : "Update Policy"}
								</Button>
								{onCancel ? (
									<Button disabled={submitting} onClick={onCancel}>
										Cancel
									</Button>
								) : null}
							</Inline>
						</Inline>
						<GetFieldValue<
							THotelPolicyFormSchema["policy_head"]
						> name="policy_head">
							{({ value }) =>
								value ? (
									<PolicyVersionView versionId={value.latest_policy.id} />
								) : (
									<Text color="muted">
										Select a {label.toLowerCase()} to see its rules.
									</Text>
								)
							}
						</GetFieldValue>
						<SubmissionError />
					</Stack>
				</form>
			)}
		</Form>
	)
}

function PolicyHeadName({ policyHead }: { policyHead: TPolicyHeadSummary }) {
	return (
		<Inline gap="3" alignItems="center">
			<Heading as="h4">{policyHead.name}</Heading>
			<Box paddingX="1" rounded="md" backgroundColor="inset">
				<Text color="muted" fontWeight="semibold">
					v{policyHead.latest_policy.version}
				</Text>
			</Box>
		</Inline>
	)
}

function NoPolicyLinked({ children }: { children: React.ReactNode }) {
	return (
		<Stack
			padding="6"
			justifyContent="center"
			alignItems="center"
			height="full"
		>
			<Text color="muted" fontWeight="semibold">
				{children}
			</Text>
		</Stack>
	)
}

export function HotelChildAges({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const canManage = hasPermission(PERMISSIONS.CREATE_HOTELS)
	const initialValues = useMemo(
		() =>
			otaChildAgesToFormValues({
				min_age: hotel.extra_bed_child_age_start,
				max_age: hotel.extra_bed_child_age_end,
				ages: hotel.ota_child_ages,
				aweb_remote_pax_type: hotel.aweb_remote_pax_type,
			}),
		[
			hotel.extra_bed_child_age_start,
			hotel.extra_bed_child_age_end,
			hotel.ota_child_ages,
			hotel.aweb_remote_pax_type,
		]
	)
	// the channel manager's pax types, only for a hotel on a channel manager
	const { remotePaxTypes: mappableRemotePaxTypes } = useHotelRemotePaxTypes(
		hotel.channel_manager
	)
	const [isUnlocked, setIsUnlocked] = useState(false)

	return (
		<Stack gap="4" paddingX={{ xs: "4", xl: "8" }} paddingY="4">
			<Heading as="h2" fontSize="lg">
				Child Age
			</Heading>
			<HotelChildAgesForm
				// remount on lock/unlock so locking discards unsaved edits
				key={String(isUnlocked)}
				initialValues={initialValues}
				canManage={canManage}
				remotePaxTypes={mappableRemotePaxTypes}
				isLocked={!isUnlocked}
				hasChannelManager={Boolean(hotel.channel_manager)}
				onUnlock={() => setIsUnlocked(true)}
				onLock={() => setIsUnlocked(false)}
				onSubmit={async (values) => {
					await hotelsXHR(xhr).saveOtaChildAges(
						hotel.id,
						formValuesToOtaChildAgesPayload(values, mappableRemotePaxTypes)
					)
					showSnackbar("Child ages updated successfully")
					setIsUnlocked(false)
					onChange()
				}}
			/>
		</Stack>
	)
}

function HotelChildAgesForm({
	initialValues,
	canManage,
	remotePaxTypes,
	isLocked,
	hasChannelManager,
	onUnlock,
	onLock,
	onSubmit,
}: {
	initialValues: TChildAgesFormValues
	canManage: boolean
	remotePaxTypes?: THotelRemotePaxTypes
	isLocked: boolean
	hasChannelManager: boolean
	onUnlock: () => void
	onLock: () => void
	onSubmit: (values: TChildAgesFormValues) => Promise<void> | void
}) {
	return (
		<Form<TChildAgesFormValues>
			initialValues={initialValues}
			validate={validateFormValues(childAgesValidationSchema)}
			mutators={{ ...arrayMutators }}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true, pristine: true }}
		>
			{({ handleSubmit, submitting, pristine }) => {
				const isUpdateDisabled = pristine || submitting
				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4" alignItems="start">
							<ChildAgesFields
								canManage={canManage && !isLocked}
								remotePaxTypes={remotePaxTypes}
								headerAction={
									canManage ? (
										<HotelConfigLockButton
											title="Edit Child Age Bands"
											action="Edit Child Age Bands"
											afterAction="updating Child Age Bands"
											rateSheetStep="include the new Child Age Bands"
											acceptLabel="Yes, unlock child age bands"
											hasChannelManager={hasChannelManager}
											isLocked={isLocked}
											onUnlock={onUnlock}
											onLock={onLock}
										/>
									) : null
								}
							/>
							<SubmissionError />
							{canManage && !isLocked ? (
								<Button
									type="submit"
									level="primary"
									disabled={isUpdateDisabled}
									style={{ opacity: isUpdateDisabled ? 0.5 : 1 }}
								>
									{submitting ? <Icons.Spinner /> : null}{" "}
									{submitting ? "Updating..." : "Update"}
								</Button>
							) : null}
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}
